import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { reviewAPI } from '@/services/api';
import type { SortDir, SortField } from '@/services/api';
import type { Email } from '@/types';
import {
  Clock,
  UserPlus,
  Globe,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Tag,
  CheckCircle,
  XCircle,
  RefreshCw,
  Inbox
} from 'lucide-react';

export interface MailboxTicket {
  id: string;
  snowflakeStatus?: string;
  createdTime: string;
  channelIcon: 'web' | 'amazon' | 'messenger';
  countryFlag: string;
  ownerBadge: { type: 'avatar'; text?: string; bg?: string } | { type: 'icon' };
  latestUpdate: string;
  customerName: string;
}

interface EDeskMailboxTableProps {
  emails?: Email[];
  activeTab?: string;
  sortBy?: SortField;
  sortDir?: SortDir;
  onSort?: (field: SortField) => void;
  onSelectTicket?: (ticket: MailboxTicket) => void;
}

const BERLIN = 'Europe/Berlin';

// Time only for today's emails, date and time for older ones, so sorted lists read clearly across days
function formatCreated(date: Date): string {
  const time = date.toLocaleTimeString('de-DE', { timeZone: BERLIN, hour: '2-digit', minute: '2-digit' });
  const day = date.toLocaleDateString('de-DE', { timeZone: BERLIN, day: '2-digit', month: '2-digit', year: 'numeric' });
  const today = new Date().toLocaleDateString('de-DE', { timeZone: BERLIN, day: '2-digit', month: '2-digit', year: 'numeric' });
  return day === today ? time : `${day} ${time}`;
}

export default function EDeskMailboxTable({
  emails = [],
  activeTab = 'Pre-sales',
  sortBy,
  sortDir,
  onSort,
  onSelectTicket,
}: EDeskMailboxTableProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const sortIcon = (field: SortField) => {
    if (sortBy !== field) return <ArrowUpDown className="w-3 h-3 text-slate-400" />;
    return sortDir === 'asc'
      ? <ArrowUp className="w-3 h-3 text-blue-600" />
      : <ArrowDown className="w-3 h-3 text-blue-600" />;
  };

  const ariaSort = (field: SortField) =>
    sortBy !== field ? 'none' : sortDir === 'asc' ? 'ascending' : 'descending';

  const safeEmails = Array.isArray(emails) ? emails : [];
  const mappedTickets: MailboxTicket[] = safeEmails.map((e) => {
    // Lists are ordered by received_at, so show that, falling back to when the row was stored
    const dateObj = new Date(e.received_at || e.created_at || Date.now());
    const timeStr = formatCreated(dateObj);
    
    let channelIcon: 'web' | 'amazon' | 'messenger' = 'web';
    if (e.platform === 'amazon') channelIcon = 'amazon';
    if (e.platform === 'ebay' || e.category?.includes('chat')) channelIcon = 'messenger';

    let flag = '🇬🇧';
    if (e.from_address?.endsWith('.de')) flag = '🇩🇪';
    if (e.from_address?.endsWith('.ie')) flag = '🇮🇪';
    if (e.from_address?.endsWith('.se')) flag = '🇸🇪';
    if (e.from_address?.endsWith('.com')) flag = '🇺🇸';

    const customer = e.from_address ? e.from_address.split('@')[0] : 'Customer';

    return {
      id: e.id,
      createdTime: timeStr,
      channelIcon: channelIcon,
      countryFlag: flag,
      ownerBadge: e.reviewed_by ? { type: 'avatar', text: e.reviewed_by.substring(0, 2).toUpperCase(), bg: 'bg-blue-600' } : { type: 'icon' },
      latestUpdate: e.subject || e.body?.substring(0, 120) || 'Email received',
      customerName: customer,
    };
  });

  const displayTickets = mappedTickets;

  const bulkApproveMutation = useMutation({
    mutationFn: (ids: string[]) => reviewAPI.bulkApprove(ids, 'Admin'),
    onSuccess: () => {
      toast.success(`Approved ${selectedIds.size} tickets`);
      setSelectedIds(new Set());
      queryClient.invalidateQueries({ queryKey: ['reviewQueue'] });
      queryClient.invalidateQueries({ queryKey: ['reviewStats'] });
    },
  });

  const bulkRejectMutation = useMutation({
    mutationFn: (ids: string[]) => reviewAPI.bulkReject(ids, 'Admin'),
    onSuccess: () => {
      toast.success(`Rejected ${selectedIds.size} tickets`);
      setSelectedIds(new Set());
      queryClient.invalidateQueries({ queryKey: ['reviewQueue'] });
      queryClient.invalidateQueries({ queryKey: ['reviewStats'] });
    },
  });

  const bulkRegenerateMutation = useMutation({
    mutationFn: (ids: string[]) => reviewAPI.bulkRegenerate(ids),
    onSuccess: () => {
      toast.success(`Regenerating ${selectedIds.size} responses...`);
      setSelectedIds(new Set());
      queryClient.invalidateQueries({ queryKey: ['reviewQueue'] });
    },
  });

  const toggleSelectAll = () => {
    if (selectedIds.size === displayTickets.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(displayTickets.map((t) => t.id)));
    }
  };

  const toggleSelectRow = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedIds(next);
  };

  const handleRowClick = (ticket: MailboxTicket) => {
    if (onSelectTicket) {
      onSelectTicket(ticket);
    } else {
      navigate(`/review/${ticket.id}`);
    }
  };

  if (displayTickets.length === 0) {
    return (
      <div className="w-full h-64 flex flex-col items-center justify-center text-slate-400 bg-white select-none">
        <Inbox className="w-10 h-10 mb-2 text-slate-300 stroke-1" />
        <p className="text-sm font-semibold text-slate-600">No tickets found in this section</p>
        <p className="text-xs text-slate-400">Incoming emails from Gmail & eBay will appear here automatically in real-time.</p>
      </div>
    );
  }

  return (
    <div className="w-full bg-white select-none relative">
      {selectedIds.size > 0 && (
        <div className="bg-[#181F38] text-white px-4 py-2 flex items-center justify-between shadow-lg text-xs animate-in slide-in-from-top sticky top-0 z-20">
          <span className="font-semibold">{selectedIds.size} tickets selected</span>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => bulkApproveMutation.mutate(Array.from(selectedIds))}
              className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded font-medium flex items-center space-x-1"
            >
              <CheckCircle className="w-3.5 h-3.5" />
              <span>Approve ({selectedIds.size})</span>
            </button>
            <button
              onClick={() => bulkRejectMutation.mutate(Array.from(selectedIds))}
              className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded font-medium flex items-center space-x-1"
            >
              <XCircle className="w-3.5 h-3.5" />
              <span>Reject ({selectedIds.size})</span>
            </button>
            <button
              onClick={() => bulkRegenerateMutation.mutate(Array.from(selectedIds))}
              className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1 rounded font-medium flex items-center space-x-1"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              <span>Regenerate ({selectedIds.size})</span>
            </button>
          </div>
        </div>
      )}

      <table className="w-full text-left border-collapse">
        <thead>
          <tr className="border-b border-slate-200 text-[12px] font-semibold text-slate-600 bg-white">
            <th className="py-3 px-3 w-10 text-center">
              <input
                type="checkbox"
                checked={selectedIds.size === displayTickets.length && displayTickets.length > 0}
                onChange={toggleSelectAll}
                className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
              />
            </th>

            <th className="py-3 px-3 min-w-[140px]" aria-sort={ariaSort('received_at')}>
              <button
                type="button"
                onClick={() => onSort?.('received_at')}
                className="flex items-center space-x-1.5 hover:text-slate-900"
              >
                <Clock className="w-3.5 h-3.5 text-slate-400" />
                <span>Created</span>
                {sortIcon('received_at')}
              </button>
            </th>

            <th className="py-3 px-3 w-12 text-center">
              <Tag className="w-3.5 h-3.5 text-slate-400 mx-auto" />
            </th>

            <th className="py-3 px-3 min-w-[80px]">
              <span>Owner</span>
            </th>

            <th className="py-3 px-3 min-w-[380px]" aria-sort={ariaSort('subject')}>
              <button
                type="button"
                onClick={() => onSort?.('subject')}
                className="flex items-center space-x-1 hover:text-slate-900"
              >
                <span>Latest update</span>
                {sortIcon('subject')}
              </button>
            </th>

            <th className="py-3 px-3 min-w-[150px]" aria-sort={ariaSort('customer')}>
              <button
                type="button"
                onClick={() => onSort?.('customer')}
                className="flex items-center space-x-1 hover:text-slate-900"
              >
                <span>Customer</span>
                {sortIcon('customer')}
              </button>
            </th>
          </tr>
        </thead>

        <tbody className="divide-y divide-slate-100 text-[13px] text-slate-800">
          {displayTickets.map((ticket) => {
            const isSelected = selectedIds.has(ticket.id);
            return (
              <tr
                key={ticket.id}
                onClick={() => handleRowClick(ticket)}
                className={`hover:bg-slate-50/80 transition-colors cursor-pointer ${
                  isSelected ? 'bg-blue-50/40' : ''
                }`}
              >
                <td className="py-4 px-3 text-center" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => toggleSelectRow(ticket.id, e as any)}
                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                  />
                </td>

                <td className="py-4 px-3 whitespace-nowrap">
                  <div className="flex items-center space-x-2.5">
                    <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0" />
                    <span className="font-semibold text-slate-900 text-[13px]">
                      {ticket.createdTime}
                    </span>
                  </div>
                </td>

                <td className="py-4 px-3 text-center">
                  <div className="flex items-center justify-center space-x-1">
                    {ticket.channelIcon === 'web' && (
                      <Globe className="w-4 h-4 text-slate-500" />
                    )}
                    {ticket.channelIcon === 'amazon' && (
                      <span className="font-bold text-xs text-slate-800 tracking-tighter">a</span>
                    )}
                    {ticket.channelIcon === 'messenger' && (
                      <span className="text-blue-600 font-bold text-[10px]">eBay</span>
                    )}
                    <span className="text-sm leading-none">{ticket.countryFlag}</span>
                  </div>
                </td>

                <td className="py-4 px-3">
                  {ticket.ownerBadge.type === 'avatar' ? (
                    <div
                      className={`w-6 h-6 rounded-full ${
                        ticket.ownerBadge.bg || 'bg-amber-500'
                      } text-white font-bold text-[10px] flex items-center justify-center shadow-xs`}
                    >
                      {ticket.ownerBadge.text}
                    </div>
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-500">
                      <UserPlus className="w-3.5 h-3.5" />
                    </div>
                  )}
                </td>

                <td className="py-4 px-3 pr-6">
                  <p className="font-medium text-slate-900 line-clamp-2 leading-snug">
                    {ticket.latestUpdate}
                  </p>
                </td>

                <td className="py-4 px-3 whitespace-nowrap">
                  <span className="font-bold text-slate-900 text-[13px]">
                    {ticket.customerName}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
